import type {
  SpeakerChannel,
  SpeakerOutcome,
  SpeakerPosition
} from "./types";

/**
 * AB#9981. Labels and formatting.
 *
 * Every label is produced HERE from a machine value, never read back out of the
 * DOM, so the page can be translated without breaking either the tests or the
 * sorting. Tests assert on the `data-*` attributes; humans read these strings.
 */

/** Kebab-case machine values for `data-*` attributes. Stable across translation. */
export const outcomeKey = (outcome: SpeakerOutcome): string =>
  outcome === "Spoke" ? "spoke" : outcome === "NoShow" ? "no-show" : "signed-up";

export const channelKey = (channel: SpeakerChannel): string =>
  channel === "Phone" ? "phone" : "in-person";

export const positionKey = (position: SpeakerPosition): string =>
  position === "For" ? "for"
    : position === "Against" ? "against"
      : position === "Neutral" ? "neutral"
        : "unset";

export const outcomeLabel = (outcome: SpeakerOutcome): string =>
  outcome === "Spoke" ? "Spoke" : outcome === "NoShow" ? "No show" : "Signed up";

export const channelLabel = (channel: SpeakerChannel): string =>
  channel === "Phone" ? "By phone" : "In person";

export const positionLabel = (position: SpeakerPosition): string =>
  position === "For" ? "For"
    : position === "Against" ? "Against"
      : position === "Neutral" ? "Neutral"
        : "Not stated";

/**
 * A duration, or the words "not timed".
 *
 * There is no zero case on purpose. The server sends null for a turn that was never
 * run and for one that was started and never ended, and the reader is told which
 * fact they are looking at. The prototype rendered 0 as a dash, which made
 * "the clerk did not run the timer" indistinguishable from "the speaker said
 * nothing".
 */
export function duration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "not timed";

  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;

  // Hand-padded rather than String.padStart, which is es2017 and this project's
  // tsconfig targets an es6 lib.
  return minutes + ":" + (rest < 10 ? "0" : "") + rest;
}

/** Whole seconds as a compact human phrase, for the time-profile tiles. */
export function durationLong(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "not timed";
  return duration(seconds);
}

type DisplayNamesLike = { of: (code: string) => string | undefined };
let languageNames: DisplayNamesLike | null | undefined;

/**
 * A language code as a reader-facing name, one form everywhere on screen.
 *
 * The same person arrives as `en` from an in-person RSVP and `en-US` from a Twilio phone
 * leg, and a roster that shows both reads as two languages. Only the BASE subtag is named,
 * so both become "English". Falls back to the uppercased base code where Intl.DisplayNames
 * is missing or does not know the code. Empty for a blank code: the caller draws the blank.
 *
 * Screen only. The CSV keeps the raw code, which is what a spreadsheet consumer filters on.
 */
export function languageName(code: string | null | undefined): string {
  const base = (code || "").trim().split(/[-_]/)[0].toLowerCase();
  if (!base) return "";

  if (languageNames === undefined) {
    try {
      // Through `any`: DisplayNames is es2020 in the TypeScript lib and this project targets es6.
      const ctor = (Intl as any).DisplayNames;
      languageNames = typeof ctor === "function" ? new ctor(["en"], { type: "language" }) : null;
    } catch {
      languageNames = null;
    }
  }

  if (languageNames) {
    try {
      const name = languageNames.of(base);
      if (name && name.toLowerCase() !== base) return name;
    } catch {
      // A malformed code throws RangeError. Fall through to the code itself.
    }
  }

  return base.toUpperCase();
}

export function integer(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/**
 * The read-at stamp, in the CUSTOMER'S zone rather than the reader's. A clerk in
 * Los Angeles reconciling a Los Angeles meeting should not have to convert from
 * whatever zone the laptop happens to be set to.
 *
 * Falls back to the browser zone if the customer's zone is missing or is not one
 * Intl recognises -- an unknown zone throws, and a report that fails to render
 * because of a time zone string is a worse outcome than one showing local time.
 */
export function stampInZone(iso: string, timeZoneId: string | null): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "";

  // Explicit components rather than dateStyle/timeStyle, which are es2020 in the
  // TypeScript lib and this project targets es6.
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  };

  if (timeZoneId) {
    try {
      return new Intl.DateTimeFormat(undefined, { ...options, timeZone: timeZoneId })
        .format(instant);
    } catch {
      // Unknown or unsupported zone. Fall through to local.
    }
  }

  return new Intl.DateTimeFormat(undefined, options).format(instant);
}

/** The zone actually used by {@link stampInZone}, for the stamp's data attribute. */
export function resolveZone(timeZoneId: string | null): string {
  if (timeZoneId) {
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: timeZoneId });
      return timeZoneId;
    } catch {
      // Fall through.
    }
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Loose but not permissive. The server validates properly; this exists so a typo
 * never leaves the browser, and so the reader is told before they wait for an email
 * that will not arrive.
 */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed)) return false;
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(trimmed);
}
